import {
  FlowchartNodeShape,
  type FlowchartNodeStyle,
} from "./parse-mermaid-flowchart";
import { DEFAULT_ACCENT_BY_SHAPE } from "./flowchart-constants";

const DEFAULT_CLASS_NAME = "default";

export interface ResolvedPresentation {
  resolvedStyle: FlowchartNodeStyle | null;
  accentColor: string;
}

interface ResolvePresentationInput {
  classNames: readonly string[];
  inlineStyle: FlowchartNodeStyle | null;
  classDefs: ReadonlyMap<string, FlowchartNodeStyle>;
  defaultAccent: string;
  includeDefaultClass: boolean;
}

function hasStyleValues(style: FlowchartNodeStyle): boolean {
  return Object.keys(style).length > 0;
}

function isFlowchartNodeStyle(
  style: FlowchartNodeStyle | null | undefined,
): style is FlowchartNodeStyle {
  return style != null;
}

function mergeStyles(
  styles: readonly FlowchartNodeStyle[],
): FlowchartNodeStyle | null {
  const merged = styles.reduce<FlowchartNodeStyle>((result, style) => {
    return { ...result, ...style };
  }, {});
  return hasStyleValues(merged) ? merged : null;
}

function getDefaultClassStyle(
  input: ResolvePresentationInput,
): FlowchartNodeStyle | null {
  if (!input.includeDefaultClass) {
    return null;
  }
  if (input.classNames.length > 0) {
    return null;
  }
  return input.classDefs.get(DEFAULT_CLASS_NAME) ?? null;
}

function getClassStyles(input: ResolvePresentationInput): FlowchartNodeStyle[] {
  return input.classNames
    .map((className) => input.classDefs.get(className))
    .filter(isFlowchartNodeStyle);
}

function getStyleList(style: FlowchartNodeStyle | null): FlowchartNodeStyle[] {
  return style ? [style] : [];
}

function getAccentColor(input: {
  resolvedStyle: FlowchartNodeStyle | null;
  defaultAccent: string;
}): string {
  const styleAccent = getStyleAccent(input.resolvedStyle);
  if (styleAccent != null) {
    return styleAccent;
  }
  return input.defaultAccent;
}

function getStyleAccent(style: FlowchartNodeStyle | null): string | null {
  if (style == null) {
    return null;
  }
  if (style.stroke != null) {
    return style.stroke;
  }
  if (style.fill != null) {
    return style.fill;
  }
  return null;
}

export function resolvePresentation(
  input: ResolvePresentationInput,
): ResolvedPresentation {
  const defaultStyle = getDefaultClassStyle(input);
  const styles = [
    ...getStyleList(defaultStyle),
    ...getClassStyles(input),
    ...getStyleList(input.inlineStyle),
  ];
  const resolvedStyle = mergeStyles(styles);
  return {
    resolvedStyle,
    accentColor: getAccentColor({
      resolvedStyle,
      defaultAccent: input.defaultAccent,
    }),
  };
}

export function getDefaultSubgraphPresentation(): ResolvedPresentation {
  return {
    resolvedStyle: null,
    accentColor: DEFAULT_ACCENT_BY_SHAPE[FlowchartNodeShape.Process],
  };
}

export function getDefaultNodePresentation(
  shape: FlowchartNodeShape,
): ResolvedPresentation {
  return {
    resolvedStyle: null,
    accentColor: DEFAULT_ACCENT_BY_SHAPE[shape],
  };
}
